namespace Solovis.Portal.TxServices {

    export type ProFormaTxGroupViewMode = "default" | "newInvestment";

    /**
     * Payload shapes passed to notifyModelChanged / the "modelChange" event it triggers.
     * One case per operation actually raised by this class and GroupedProFormaTxViewEditModel.
     */
    export type ModelChangeEventData =
        | { operation: "primaryTransactionEditIdChanged"; primaryTransactionEditId: string | null }
        | { operation: "primaryTransactionEntryIdChanged"; primaryTransactionEntryId: number | null }
        | { operation: "groupedProFormaTxViewEditModelsChanged"; groupedProFormaTxViewEditModels: GroupedProFormaTxViewEditModel[] }
        | { operation: "transactionEdit"; transaction: GroupedProFormaTxViewEditModel }
        | { operation: "editTransaction"; groupedProFormaTxViewEditModel: GroupedProFormaTxViewEditModel }
        | { operation: "addTransaction"; groupedProFormaTxViewEditModel: GroupedProFormaTxViewEditModel }
        | { operation: "deleteTransaction"; groupedProFormaTxViewEditModel: GroupedProFormaTxViewEditModel }
        | { operation: "unlinkTransaction"; groupedProFormaTxViewEditModel: GroupedProFormaTxViewEditModel }
        | { operation: "applyTransactionModelUpdates" }
        | { operation: "revertedAndLocked" };

    export class GroupedProFormaTxListViewEditModel {

        private _portalServices: Solovis.Portal.IPortalServices;

        private _investmentId;
        private _primaryTransactionEditId;
        private _primaryTransactionEntryId;
        private _originalPrimaryTransactionEntryId;
        private _entries;
        private _groupedProFormaTxViewEditModels: GroupedProFormaTxViewEditModel[];

        private _modelEnvironment: InvData.ModelEnvironment;
        private _canEdit: boolean;
        private _locked: boolean;
        private _viewMode: ProFormaTxGroupViewMode;

        public investmentSummaryManager?: TxInvestmentSummaryManager;
        public cachedExchangeRateManager?: TxCachedExchangeRateManager;
        public cachedPriceManager?: TxCachedPriceManager;
        public cachedMarketValueManager?: TxCachedMarketValueManager;

        public validationResults;
        private _deletedTransactionIds;
        private _unlinkedTransactionIds;

        private _isDirty;

        constructor(options) {
            var self = this;

            if (options.portalServices) {
                this._portalServices = options.portalServices;
            }
            else {
                this._portalServices = new Solovis.Portal.SitePortalServices();
            }

            this._investmentId = options.investmentId ?? null;
            this._primaryTransactionEditId = options.primaryTransactionEditId ?? null;
            this._primaryTransactionEntryId = options.primaryTransactionEntryId ?? null;
            this._originalPrimaryTransactionEntryId = options.primaryTransactionEntryId ?? null;
            this._entries = options.entries ?? null;
            this._groupedProFormaTxViewEditModels = !_.isEmpty(options.groupedProFormaTxViewEditModels) ? options.groupedProFormaTxViewEditModels : [];

            this._modelEnvironment = options.modelEnvironment ?? null;
            this._canEdit = Solovis.isNotNull(options.canEdit) ? (options.canEdit ? true : false) : true;
            this._locked = !!options.locked;
            // Default to "default" when an investment context was supplied; otherwise the editor
            // was opened without one (new-investment flow). The grid control overrides this when
            // the user toggles views.
            this._viewMode = Solovis.isNotNull(this._investmentId) ? "default" : "newInvestment";
            if (this._primaryTransactionEntryId && this._primaryTransactionEditId) {
                this._primaryTransactionEditId = null;
            }
            if (this._entries) {
                $(this._entries).each(function (i, txEntryData) {
                    var groupedProFormaTxViewEditModel = self._createEditModelFromEntry(txEntryData);
                    self._groupedProFormaTxViewEditModels.push(groupedProFormaTxViewEditModel);
                });
            }

            this.investmentSummaryManager = new TxInvestmentSummaryManager({
                portalServices: this._portalServices
            });
            $(options.investmentSummaries).each(function (i, summary: any) {
                self.investmentSummaryManager.registerSummary(summary);
            });

            this.cachedExchangeRateManager = new TxCachedExchangeRateManager({
                portalServices: this._portalServices
            });
            this.cachedPriceManager = new TxCachedPriceManager({
                portalServices: this._portalServices
            });
            this.cachedMarketValueManager = new TxCachedMarketValueManager({
                portalServices: this._portalServices
            });

            // Ensure we do an initial ordering based on primary tx.
            this._orderGroupedProFormaTxViewEditModels();
        }


        public portalServices() {
            return this._portalServices;
        }

        public getPropDefIdsToEdit(): number[] {
            const modelEnv = this.modelEnvironment();
            return TxSettings.getCustomPropIdsToEditForModelEnvironment(this._portalServices, modelEnv);
        }


        public _createEditModelFromEntry(txEntryData) {
            var editModelOptions: GroupedProFormaTxViewEditModelOptions = {
                groupedProFormaTxListViewEditModel: this
            };

            const txModelClone: TxServices.TransactionEntryEditModel = ({} as any);
            for (const propertyName in txEntryData) {
                txModelClone[propertyName] = txEntryData[propertyName];
            }

            editModelOptions.tx = txModelClone;

            const groupedProFormaTxViewEditModel = new GroupedProFormaTxViewEditModel(editModelOptions);

            return groupedProFormaTxViewEditModel;
        }


        public modelEnvironment(modelEnvironment?: InvData.ModelEnvironment): InvData.ModelEnvironment {
            if (!arguments.length) {
                return this._modelEnvironment;
            }
            else {
                this._modelEnvironment = modelEnvironment;
            }
            return null;
        };

        public length() {
            return this._groupedProFormaTxViewEditModels.length;
        }

        public investmentId(investmentId?: typeof this._investmentId) {
            if (!arguments.length) {
                return this._investmentId;
            }
            else {
                this._investmentId = investmentId;
            }
            return null;
        }

        public primaryTransactionEditId(primaryTransactionEditId?: typeof this._primaryTransactionEditId) {
            if (!arguments.length) {
                return this._primaryTransactionEditId;
            }
            else {
                if (Solovis.isNotNull(this._primaryTransactionEntryId)) {
                    console.log("Cannot update edit id when transaction Id exists");
                }
                else {
                    this._primaryTransactionEditId = primaryTransactionEditId;

                    this.notifyModelChanged({
                        operation: "primaryTransactionEditIdChanged",
                        primaryTransactionEditId: this._primaryTransactionEditId
                    });
                }
            }
            return null;
        }

        /**
         * The group's primary tx id as loaded from the server -- unlike primaryTransactionEntryId(),
         * this never changes locally (e.g. via Make Primary), so it's the stable id to re-query the
         * server about this group's current state (see revertToServerStateAndLock()).
         */
        public originalPrimaryTransactionEntryId() {
            return this._originalPrimaryTransactionEntryId;
        }

        public primaryTransactionEntryId(primaryTransactionEntryId?: typeof this._primaryTransactionEntryId) {
            if (!arguments.length) {
                return this._primaryTransactionEntryId;
            }
            else {
                this._primaryTransactionEntryId = primaryTransactionEntryId;
                this._primaryTransactionEditId = null;
                this._orderGroupedProFormaTxViewEditModels();

                this.notifyModelChanged({
                    operation: "primaryTransactionEntryIdChanged",
                    primaryTransactionEntryId: this._primaryTransactionEntryId
                });
            }
            return null;
        }

        public groupedProFormaTxViewEditModels(groupedProFormaTxViewEditModels?: GroupedProFormaTxViewEditModel[]): GroupedProFormaTxViewEditModel[] {
            if (!arguments.length) {
                return this._groupedProFormaTxViewEditModels;
            }
            else {
                this._groupedProFormaTxViewEditModels = groupedProFormaTxViewEditModels;
                this.notifyModelChanged({
                    operation: "groupedProFormaTxViewEditModelsChanged",
                    groupedProFormaTxViewEditModels: this._groupedProFormaTxViewEditModels
                });
            }
            return null;
        }

        public canEdit(canEdit?: typeof this._canEdit) {
            if (!arguments.length) {
                return this._canEdit;
            }
            else {
                this._canEdit = !!canEdit;
            }
            return null;
        }

        /**
         * True if any transaction in this group is approved or promoted (PF2-015). The grid
         * control uses this to disable the Delete action even when canEdit() is true, since a
         * locked group can still have been opened for editing before its state changed.
         */
        public locked(): boolean {
            return this._locked;
        }

        /**
         * Rebuilds this group from a fresh server response (see PF2-015) and locks it for further
         * editing. Used when a save attempt was rejected because the group was approved/promoted
         * out from under an already-open dialog: rather than reverting to the dialog's original
         * load-time snapshot (which could discard an earlier, already-persisted save in this same
         * session), this re-fetches current server truth so nothing legitimately saved is lost.
         */
        public revertToServerStateAndLock(entries: any[]) {
            const self = this;

            this._groupedProFormaTxViewEditModels = (entries ?? []).map(
                (txEntryData) => self._createEditModelFromEntry(txEntryData));
            this._deletedTransactionIds = null;
            this._unlinkedTransactionIds = null;
            this._canEdit = false;
            this._locked = true;

            this._orderGroupedProFormaTxViewEditModels();

            $(this).trigger("modelChange", { operation: "revertedAndLocked" });
            this.setIsDirty(false);
        }

        public viewMode(viewMode?: ProFormaTxGroupViewMode): ProFormaTxGroupViewMode {
            if (!arguments.length) {
                return this._viewMode;
            }
            this._viewMode = viewMode;
            return null;
        }


        public getTagById(tagId: number) {
            if (Solovis.isNull(this._modelEnvironment)) {
                throw "Default tag set is not loaded.";
            }

            const tagSet = this._modelEnvironment.tagSets.tryGetById(GroupedProFormaTxViewEditModel.defaultTxTypeTagSetId);
            const foundTag = tagSet.tryGetTagById(tagId);

            if (Solovis.isNull(foundTag)) {
                throw "Tag Id " + tagId + " not found in default tag set.";
            }

            return foundTag;
        }

        public mapEditIdsToTxIds(map) {
            const self = this;

            const groupModel = this.getTransactionGroupModel();
            if (groupModel.success) {
                $(map).each(function () {
                    const mapEntry = this;

                    if (self._groupedProFormaTxViewEditModels) {
                        for (const tx of self._groupedProFormaTxViewEditModels) {
                            if (tx.editId === mapEntry.editId) {
                                if (tx.editId === groupModel.groupedTxListModel.primaryTx?.editId) {
                                    self._primaryTransactionEntryId = mapEntry.txId;
                                    self._primaryTransactionEditId = null;
                                }                                
                                tx.transactionEntryId(mapEntry.txId);
                                delete tx.editId;
                            }
                        }
                    }                   
                });
            }
        }

        public getTransactionGroupModel(): {
            success: boolean;
            groupedTxListModel: ProFormaTxGroupUpdateModel;
            message?: string
        }{
            this._doValidate();

            const groupedTxListModel: ProFormaTxGroupUpdateModel = {
                primaryTx: null,
                originalPrimaryTxId: this._originalPrimaryTransactionEntryId,
                secondaryTxList: [],
                txIdsToDelete: []
            };

            const result = {
                success: this.validationResults.isValid,
                groupedTxListModel: groupedTxListModel,
                message: this.validationResults.message
            }

            if (this._groupedProFormaTxViewEditModels) {
                for (const groupedProFormaTxViewEditModel of this._groupedProFormaTxViewEditModels) {
                    if (groupedProFormaTxViewEditModel.isPrimary()) {
                        groupedTxListModel.primaryTx = groupedProFormaTxViewEditModel.getTransactionDataToSave();
                    }
                    else {
                        groupedTxListModel.secondaryTxList?.push(groupedProFormaTxViewEditModel.getTransactionDataToSave());
                    }
                }
            }

            if (!_.isEmpty(this._deletedTransactionIds)) {
                $(this._deletedTransactionIds).each(function () {
                    groupedTxListModel.txIdsToDelete?.push(Number(this));
                });
            }

            if (_.isEmpty(this._groupedProFormaTxViewEditModels) && _.isEmpty(this._deletedTransactionIds)) {
                result.success = false;
                result.message = "The transaction group is empty.";
            }

            return result;
        }

        public _doValidate() {
            const self = this;
            this.validationResults = {
                isValid: true,
                message: null
            }

            if (Solovis.isNull(this.primaryTransactionEntryId()) &&
                Solovis.isNull(this.primaryTransactionEditId())) {
                this.validationResults.isValid = false;
                this.validationResults.message = "Primary transaction entry is not set.";
            }
            else {
                // The active grid view dictates which fields are required: Default view validates
                // Investment + Effective Date; New Investment view validates Owner + Holding +
                // Effective Date. The grid control keeps _viewMode in sync with the toggle.
                const isNewInvestmentView = this._viewMode === "newInvestment";

                let missingEffectiveDate = false;
                let missingInvestment = false;
                let missingOwner = false;
                let missingHolding = false;

                const txModels = this.groupedProFormaTxViewEditModels();
                for (const txModel of txModels) {
                    if (isNewInvestmentView) {
                        if (Solovis.isNull(txModel.ownerId())) {
                            missingOwner = true;
                        }
                        if (Solovis.isNull(txModel.holdingId())) {
                            missingHolding = true;
                        }
                    }
                    else if (Solovis.isNull(txModel.investmentId())) {
                        missingInvestment = true;
                    }
                    if (Solovis.isNull(txModel.effectiveDayId())) {
                        missingEffectiveDate = true;
                    }
                }

                if (missingEffectiveDate || missingInvestment || missingOwner || missingHolding) {
                    const missingFields = [];
                    if (missingOwner) {
                        missingFields.push("Owner");
                    }
                    if (missingHolding) {
                        missingFields.push("Holding");
                    }
                    if (missingInvestment) {
                        missingFields.push("Investment");
                    }
                    if (missingEffectiveDate) {
                        missingFields.push("Effective Date");
                    }

                    let joined: string;
                    if (missingFields.length === 1) {
                        joined = missingFields[0];
                    }
                    else if (missingFields.length === 2) {
                        joined = `${missingFields[0]} and ${missingFields[1]}`;
                    }
                    else {
                        joined = `${missingFields.slice(0, -1).join(", ")}, and ${missingFields[missingFields.length - 1]}`;
                    }

                    const errorMessage = `${joined} ${((missingFields.length > 1) ? "are" : "is")} required for all transactions.`;

                    this.validationResults.isValid = false;
                    this.validationResults.message = errorMessage;
                }
            }
        }

        public hasTxWithMissingTxType() {
            const txModels = this.groupedProFormaTxViewEditModels();
            for (const txModel of txModels) {
                if (Solovis.isNull(txModel.transactionTypeTagId())) {
                    return true;
                }
            }

            return false;
        }

        /**
         * A pro forma transaction group is cash-balanced when the sum of Net Amount In
         * across every transaction in the group is zero. Non-numeric or blank entries
         * count as zero — required-field validation is handled elsewhere.
         */
        public hasUnbalancedCash() {
            const txModels = this.groupedProFormaTxViewEditModels();
            let total = 0;
            for (const txModel of txModels) {
                const raw = txModel.netAmountIn();
                if (Solovis.isNull(raw)) {
                    continue;
                }
                const asString = typeof raw === "number" ? raw.toString() : raw;
                const parsed = parseFloat(asString.replace(/,/g, ""));
                if (!isNaN(parsed)) {
                    total += parsed;
                }
            }

            // Tolerate floating-point noise — values are decimals from text input.
            return Math.abs(total) > 1e-6;
        }

        public _orderGroupedProFormaTxViewEditModels() {
            const tempArray = [];
            let primaryTx = null;
            // The primary transaction always goes first for convenience's sake.

            for (let i = 0; i < this._groupedProFormaTxViewEditModels.length; i++) {
                if (this._groupedProFormaTxViewEditModels[i].isPrimary()) {
                    primaryTx = this._groupedProFormaTxViewEditModels[i];
                }
                else {
                    tempArray.push(this._groupedProFormaTxViewEditModels[i]);
                }
            }

            if (Solovis.isNotNull(primaryTx)) {
                tempArray.splice(0, 0, primaryTx);
            }

            // If we are ordering a list after deleting the primary, mark the first one as primary (if it exists).
            else {
                if (this._groupedProFormaTxViewEditModels.length > 0) {
                    const txEntryId = this._groupedProFormaTxViewEditModels[0].transactionEntryId();
                    if (Solovis.isNotNull(txEntryId)) {
                        this._primaryTransactionEntryId = txEntryId;
                        this._primaryTransactionEditId = null;
                    }
                    else {
                        const txEditId = this._groupedProFormaTxViewEditModels[0].editId;
                        this._primaryTransactionEditId = txEditId;
                    }
                }
            }

            this._groupedProFormaTxViewEditModels = tempArray;
        }

        public notifyModelChanged(options: ModelChangeEventData) {
            this.setIsDirty(true);
            $(this).trigger("modelChange", options);
        }

        public addTransaction(groupedProFormaTxViewEditModel, skipValidation: boolean = false) {
            this.setIsDirty(true);

            const validationResults = !skipValidation ? groupedProFormaTxViewEditModel.validate() : undefined;

            if (skipValidation || validationResults.isValid) {
                // Check to see if the edit model exists.  if it does, overwrite it.
                if (this.containsEditModel(groupedProFormaTxViewEditModel)) {
                    var txIndex = this.indexOf(groupedProFormaTxViewEditModel);

                    if (txIndex != -1) {
                        this._groupedProFormaTxViewEditModels.splice(txIndex, 1, groupedProFormaTxViewEditModel);
                        this.notifyModelChanged({
                            operation: "editTransaction",
                            groupedProFormaTxViewEditModel: groupedProFormaTxViewEditModel,
                        });
                    }
                    else {
                        throw "GroupedProFormaTxViewEditModel index not found.";
                    }
                }
                else {
                    // This is a new model.
                    this._groupedProFormaTxViewEditModels.push(groupedProFormaTxViewEditModel);
                    this.notifyModelChanged({
                        operation: "addTransaction",
                        groupedProFormaTxViewEditModel: groupedProFormaTxViewEditModel,
                    });
                }

                // If this is the first Tx added, then it's the primary.
                if (this._groupedProFormaTxViewEditModels.length === 1) {
                    const txId = groupedProFormaTxViewEditModel.transactionEntryId();
                    if (Solovis.isNotNull(txId)) {
                        this._primaryTransactionEntryId = txId;
                        this._primaryTransactionEditId = null;
                    }
                    else {
                        const txEditId = groupedProFormaTxViewEditModel.editId;
                        if (Solovis.isNotNull(txEditId)) {
                            this._primaryTransactionEditId = txEditId;
                        }
                    }
                }
            }
            else {
                console.log("Attempted to add an invalid transaction.  Error message: " + validationResults.errorMessages.join(" "));
            }
        }

        /**
         * Validates every transaction in the group and aggregates the distinct
         * failures. Used to gate saving so the user is prevented from saving until
         * all invalid pairs (e.g. matching Owner and Holding) are corrected. Each
         * distinct error is shown once and is not tied to a specific transaction.
         */
        public validateAll() {
            const errorMessages: string[] = [];

            for (const groupedProFormaTxViewEditModel of this._groupedProFormaTxViewEditModels) {
                const result = groupedProFormaTxViewEditModel.validate();
                for (const message of result.errorMessages) {
                    if (errorMessages.indexOf(message) === -1) {
                        errorMessages.push(message);
                    }
                }
            }

            return {
                isValid: errorMessages.length === 0,
                errorMessage: errorMessages.join("<br>")
            };
        }

        public indexOf(groupedProFormaTxViewEditModel: GroupedProFormaTxViewEditModel) {
            let txIndex = -1;
            const id = groupedProFormaTxViewEditModel.getId();

            $(this._groupedProFormaTxViewEditModels).each(function (index) {
                if (this.equalById(groupedProFormaTxViewEditModel)) {
                    txIndex = index;
                }
            });

            return txIndex;
        }

        public containsEditModel(groupedProFormaTxViewEditModel: GroupedProFormaTxViewEditModel) {
            const txEntryId = groupedProFormaTxViewEditModel.transactionEntryId();
            const txEditId = groupedProFormaTxViewEditModel.editId;
            let found = false;
            if (Solovis.isNotNull(txEntryId)) {
                $(this._groupedProFormaTxViewEditModels).each(function () {
                    if (this.transactionEntryId() === txEntryId) {
                        found = true;
                    }
                });
            }
            else if (Solovis.isNotNull(txEditId)) {
                $(this._groupedProFormaTxViewEditModels).each(function () {
                    if (this.editId === txEditId) {
                        found = true;
                    }
                });
            }
            return found;
        }

        public deleteTransaction(groupedProFormaTxViewEditModel: GroupedProFormaTxViewEditModel) {
            const txId = groupedProFormaTxViewEditModel.transactionEntryId();

            if (txId) {
                if (Solovis.isNull(this._deletedTransactionIds)) {
                    this._deletedTransactionIds = [];
                }
                this._deletedTransactionIds.push(txId);

                this.notifyModelChanged({
                    operation: "deleteTransaction",
                    groupedProFormaTxViewEditModel: groupedProFormaTxViewEditModel,
                });
            }


            this.unlinkTransaction(groupedProFormaTxViewEditModel);
        }


        public unlinkTransaction(groupedProFormaTxViewEditModel: GroupedProFormaTxViewEditModel) {
            this.setIsDirty(true);

            const txIndex = this.indexOf(groupedProFormaTxViewEditModel);

            if (txIndex != -1) {
                this._groupedProFormaTxViewEditModels.splice(txIndex, 1);

                const txId = groupedProFormaTxViewEditModel.transactionEntryId();

                if (txId) {
                    if (Solovis.isNull(this._unlinkedTransactionIds)) {
                        this._unlinkedTransactionIds = [];
                    }
                    this._unlinkedTransactionIds.push(txId);
                }

                this.notifyModelChanged({
                    operation: "unlinkTransaction",
                    groupedProFormaTxViewEditModel: groupedProFormaTxViewEditModel,
                });
            }
            else {
                console.log("Unexpected: transaction not found.");
            }
        }


        public getProFormaTxViewEditModelForPrimaryTransaction(): GroupedProFormaTxViewEditModel {
            const txModels = this.groupedProFormaTxViewEditModels();

            const primaryTxIndex = _.findIndex(txModels, function (tx: any) {
                return tx.isPrimary();
            });

            if (primaryTxIndex >= 0) {
                return txModels[primaryTxIndex];
            }

            return null;
        }

        public getProFormaTxViewEditModelForEditId(editId: string): GroupedProFormaTxViewEditModel {
            let matchedModel = null;

            this._groupedProFormaTxViewEditModels.forEach(function (model) {
                if (model.editId == editId) {
                    matchedModel = model;
                }
            });

            return matchedModel;
        }


        public getProFormaTxViewEditModelForTransactionEntryId(txEntryId: number): GroupedProFormaTxViewEditModel {
            let matchedModel = null;

            this._groupedProFormaTxViewEditModels.forEach(function (model) {
                if (model.transactionEntryId() == txEntryId) {
                    matchedModel = model;
                }
            });

            return matchedModel;
        }


        public applyTransactionModelUpdates(txModelDataList) {
            const self = this;

            txModelDataList.forEach(function (txModelData: any) {
                let txEditModel = null;
                if (txModelData.transactionEntryId) {
                    txEditModel = self.getProFormaTxViewEditModelForTransactionEntryId(txModelData.transactionEntryId);
                }
                else if (txModelData.editId) {
                    txEditModel = self.getProFormaTxViewEditModelForEditId(txModelData.editId);
                }

                if (txEditModel) {
                    // TODO: Apply changes to tx models (apply other properties).
                    txEditModel.netAmountIn(txModelData.netAmountIn);
                }
                else {
                    // New transaction so add it to the model.
                    txEditModel = self._createEditModelFromEntry(txModelData);
                    self._groupedProFormaTxViewEditModels.push(txEditModel);
                }
            });

            this.notifyModelChanged({
                operation: "applyTransactionModelUpdates"
            });
        }


        public setIsDirty(isDirty) {
            this._isDirty = isDirty;
        }

        public isDirty() {
            if (this._isDirty) {
                return true;
            }
            else {
                return false;
            }
        }
    }

}